import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as TooltipUI } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';
import { formatSeconds } from '@/lib/utils';
import { WidgetData } from '@/types';
import dayjs from 'dayjs';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

// Sourced from POST /api/widgets/key-metrics widget type 13. The backend already resolves
// bucket granularity (Hourly/Daily/Weekly/Monthly/Quarterly/Yearly) and returns a
// ready-to-display `label` per point (e.g. "Week29 2026", "Jul 2026", "Quarter1 2026",
// "2026") — the client just renders it as-is, no client-side spacing/label inference needed.
// `value` is AWT in whole seconds; formatted as HH:MM:SS via formatSeconds. There is no
// previous-period series: the backend accepts but does not use previousStart/previousEnd
// for this widget.
interface WaitingTimeDataPoint {
  label: string;
  value: number;
}

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
  dataKey: keyof WaitingTimeDataPoint;
  payload: WaitingTimeDataPoint;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  theme?: string;
}

interface AverageWaitingTimeTrendWidgetProps {
  data: WidgetData;
  isGeneralMode?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string;
  dateRange?: { from: Date; to: Date };
}

const generatePlaceholderBuckets = (dateRange?: { from: Date; to: Date }): WaitingTimeDataPoint[] => {
  if (!dateRange) return [];
  const buckets: WaitingTimeDataPoint[] = [];
  let cursor = dayjs(dateRange.from).startOf('day');
  const end = dayjs(dateRange.to).startOf('day');
  while (cursor.isSame(end) || cursor.isBefore(end)) {
    buckets.push({ label: cursor.format('MM/DD/YY'), value: 0 });
    cursor = cursor.add(1, 'day');
  }
  return buckets;
};

const CustomTooltip = ({ active, payload, theme }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  return (
    <div className={`border rounded-lg shadow-md p-3 min-w-[180px] ${theme === "dark"
      ? "bg-gray-800 border-gray-600 text-white"
      : "bg-white border-gray-200 text-gray-900"
      }`}>
      {payload.map((item) => (
        <div key={item.dataKey} className="text-sm" style={{ color: item.color }}>
          Average Wait Time: {formatSeconds(Number(item.value))}
        </div>
      ))}
    </div>
  );
};

const ChartContent = ({ data, theme }: { data: WaitingTimeDataPoint[], theme: string }) => {
  const isAllDataZero = data.every(item =>
    item.value === 0 || item.value == null
  );

  return (
    <ResponsiveContainer width="99%" height={280}>
      <LineChart data={data} margin={{ left: 20, right: 30, top: 20, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
        />
        <YAxis
          tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
          tickFormatter={formatSeconds}
          domain={isAllDataZero ? [0, 180] : [0, 'dataMax']}
          ticks={isAllDataZero ? [0, 30, 60, 90, 120, 150, 180] : undefined}
          allowDecimals={false}
        />
        <Tooltip
          content={<CustomTooltip theme={theme} />}
          cursor={{ stroke: theme === "dark" ? '#4b5563' : '#d1d5db' }}
        />
        {!isAllDataZero && (
          <Line
            type="monotone"
            dataKey="value"
            name=""
            stroke={theme === "dark" ? '#1681F6' : '#23407a'}
            strokeWidth={2}
            dot={{ r: 5, stroke: theme === "dark" ? '#1681F6' : '#23407a', strokeWidth: 2, fill: theme === "dark" ? '#0f172a' : 'white' }}
            activeDot={{ r: 7, fill: theme === "dark" ? '#1681F6' : '#23407a', stroke: theme === "dark" ? '#1681F6' : '#23407a', strokeWidth: 2 }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

const WidgetHeader = () => (
  <CardHeader>
    <div className="flex items-center gap-2">
      <span>Average Wait Time Trend</span>
      <TooltipProvider>
        <TooltipUI delayDuration={0}>
          <TooltipTrigger asChild>
            <span className="cursor-pointer">
              <Info className="h-4 w-4 text-foreground" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="max-w-xs text-xs font-normal">
              The pattern of average amount of time that the callers had to wait for an agent to answer within a given time.
            </p>
          </TooltipContent>
        </TooltipUI>
      </TooltipProvider>
    </div>
  </CardHeader>
);

const AverageWaitingTimeTrendWidget: React.FC<AverageWaitingTimeTrendWidgetProps> = ({
  data,
  loading,
  dateRange
}) => {
  const { theme } = useDashboard();

  const chartData = useMemo(() => {
    const rawData = data.value as WaitingTimeDataPoint[];
    if (Array.isArray(rawData) && rawData.length > 0) {
      return rawData;
    }
    return generatePlaceholderBuckets(dateRange);
  }, [data.value, dateRange]);

  const renderContent = () => {
    if (loading) return <Skeleton className="w-full h-[300px] rounded-none" />;
    return <ChartContent data={chartData} theme={theme} />;
  };

  return (
    <Card className="w-full overflow-hidden min-h-[300px] dark:border-0">
      <WidgetHeader />
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          {renderContent()}
        </div>
      </CardContent>
    </Card>
  );
};

export default AverageWaitingTimeTrendWidget;
